/**
 * Typed `fetch`-based API client.
 *
 * Preserves the Yaarikut response envelope (`{ success, message, data, code }`)
 * and matches the header / token conventions of the old CRA client
 * (`REACTJS/src/app/utils/Api.js`):
 *   - `api-key` header from `import.meta.env.VITE_API_KEY`
 *   - `Authorization: Bearer <token>` when a token is available
 *
 * On `success: true` the envelope is unwrapped and `data` is returned (typed).
 * On `success: false` (or a non-OK HTTP status) an `ApiError` is thrown.
 * A 401/403 (HTTP status OR envelope `code`) triggers the registered
 * `onUnauthorized()` handler before throwing, so the auth store can log out.
 */

import type { ApiResponse } from '@/types/api';

/** Base URL already ends with `/` (e.g. `http://localhost:4000/api/`). */
const BASE_URL = import.meta.env.VITE_API_URL;
const API_KEY = import.meta.env.VITE_API_KEY;

// Loud check — `buildUrl()` joins `BASE_URL + clean` where `clean`
// has leading slashes stripped, so a missing trailing slash on
// `VITE_API_URL` produces `/apiuser/list` instead of `/api/user/list`
// and every request silently 404s. Surface this at module-load
// time (dev only) so the misconfiguration is caught immediately
// instead of triggering hundreds of broken requests at runtime.
if (import.meta.env.DEV && typeof BASE_URL === 'string' && BASE_URL && !BASE_URL.endsWith('/')) {
  // eslint-disable-next-line no-console
  console.error(
    `[api] VITE_API_URL ("${BASE_URL}") must end with "/". Add the trailing slash to .env (or the dev:https script) — every API call is currently producing a malformed URL.`,
  );
}

/** Thrown when the request fails (envelope `success: false` or HTTP error). */
export class ApiError extends Error {
  code: number;
  data: unknown;

  constructor(message: string, code: number, data: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.data = data;
    // Restore prototype chain for instanceof checks across transpilation.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Auth integration points. Default implementations are no-ops so the client
 * has no static dependency on the auth store (avoids a circular import); the
 * auth store calls `configureAuth` at startup to wire these up.
 */
export interface AuthHandlers {
  getToken: () => string | null;
  onUnauthorized: () => void;
}

const handlers: AuthHandlers = {
  getToken: () => null,
  onUnauthorized: () => {},
};

/** Register (partial) auth handlers. Unspecified handlers keep their defaults. */
export function configureAuth(next: Partial<AuthHandlers>): void {
  if (next.getToken) handlers.getToken = next.getToken;
  if (next.onUnauthorized) handlers.onUnauthorized = next.onUnauthorized;
}

/**
 * Read the currently-configured bearer token. Mainly for non-fetch transports
 * (e.g. the XHR-based `uploadWithProgress`) that need the same auth state.
 */
export function getAuthToken(): string | null {
  return handlers.getToken();
}

/** Trigger the registered unauthorized handler (used by alternate transports). */
export function notifyUnauthorized(): void {
  handlers.onUnauthorized();
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * Join the (slash-terminated) base URL with a relative path.
 * Re-exported so `uploadWithProgress` can share the exact same
 * joining rules — they used to be duplicated, and `VITE_API_URL=/api/`
 * surfaced enough edge cases that drift between the two helpers
 * became a real risk.
 */
export function buildUrl(path: string): string {
  const clean = path.replace(/^\/+/, '');
  if (clean.includes('://')) return clean;
  return BASE_URL + clean;
}

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

async function request<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (API_KEY) headers.set('api-key', API_KEY);

  const token = handlers.getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let payload: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    if (isFormData(body)) {
      // Let the browser set the multipart boundary; do NOT set Content-Type.
      payload = body;
    } else {
      headers.set('Content-Type', 'application/json');
      payload = JSON.stringify(body);
    }
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      ...init,
      method,
      headers,
      body: payload,
    });
  } catch (err) {
    // Network failure / CORS / abort — no envelope to unwrap.
    throw new ApiError(
      err instanceof Error ? err.message : 'Network request failed',
      0,
      null,
    );
  }

  // Attempt to parse the JSON envelope; tolerate empty/non-JSON bodies.
  let envelope: Partial<ApiResponse<T>> | null = null;
  const text = await response.text();
  if (text) {
    try {
      envelope = JSON.parse(text) as ApiResponse<T>;
    } catch {
      envelope = null;
    }
  }

  const code = envelope?.code ?? response.status;
  const success = envelope?.success ?? response.ok;

  // Only treat this as a token-expired signal when the envelope itself
  // reports failure with code 401. Legitimate `{ success: true, code: 403 }`
  // envelopes ("permission denied" responses that the UI handles inline)
  // and 403 token-rejection responses are NOT auto-logged-out — the UI
  // can decide how to render them. This matches the legacy CRA client.
  if (code === 401 && envelope && envelope.success === false) {
    handlers.onUnauthorized();
  }

  // Tolerate a 2xx response with an empty body: some endpoints (e.g.
  // fire-and-forget actions) intentionally return no payload. Previously
  // this fell through to the error branch and threw "Unable to process
  // request" even though the request succeeded.
  if (response.ok && envelope === null && !text) {
    return null as T;
  }

  if (!success || envelope === null) {
    const message =
      envelope?.message ??
      (response.ok ? 'Unable to process request' : response.statusText) ??
      'Request failed';
    throw new ApiError(message, code, envelope?.data ?? null);
  }

  return envelope.data as T;
}

export const api = {
  get<T>(path: string, init?: RequestInit): Promise<T> {
    return request<T>('GET', path, undefined, init);
  },
  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return request<T>('POST', path, body, init);
  },
  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return request<T>('PUT', path, body, init);
  },
  del<T>(path: string, init?: RequestInit): Promise<T> {
    return request<T>('DELETE', path, undefined, init);
  },
  /** Alias for `del` — some feature hooks prefer the standard verb name. */
  delete<T>(path: string, init?: RequestInit): Promise<T> {
    return request<T>('DELETE', path, undefined, init);
  },
  patch<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return request<T>('PATCH', path, body, init);
  },
};
